import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import './SubscriptionPlans.css';

const SubscriptionPlans = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();
  const hasChecked = useRef(false);
  
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [error, setError] = useState(null);
  const [isYearly, setIsYearly] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [statusMessage, setStatusMessage] = useState('');
  const [redirectMessage, setRedirectMessage] = useState('');

  const plans = [
    {
      id: 'basic',
      name: 'Basic',
      monthlyPrice: 35,
      yearlyPrice: 385,
      monthlyPriceId: 'price_1SbDP4P2MQvVS2rX4yotqIke',
      yearlyPriceId: 'price_1SbDP4P2MQvVS2rXtaI60FTt',
      role: 'basic_subscriber',
      maxUsers: 1,
      features: [
        'Unlimited Appointment Scheduling',
        'Unlimited Basic Client Profiles',
        'Client Management',
        'Digital Consent Forms',
        'Customizable Consent Forms',
        'Basic Inventory Management',
        'Email Support'
      ]
    },
    {
      id: 'standard',
      name: 'Standard',
      monthlyPrice: 85,
      yearlyPrice: 935,
      monthlyPriceId: 'price_1SbDP2P2MQvVS2rXvCV9eHIK',
      yearlyPriceId: 'price_1SbDP2P2MQvVS2rXTUSYzeER',
      role: 'standard_subscriber',
      maxUsers: 1,
      popular: true,
      features: [
        'Everything in Basic',
        'Advanced Client Profiles',
        'Appointment Reminders (Email)',
        'Automated Aftercare Emails',
        'Booking Enhancements',
        'Low Stock Alerts',
      ]
    },
    {
      id: 'starter_studio',
      name: 'Starter Studio',
      monthlyPrice: 199,
      yearlyPrice: 2189,
      monthlyPriceId: 'price_1SbDP1P2MQvVS2rXTMkqz2tZ',
      yearlyPriceId: 'price_1SbDP1P2MQvVS2rXE0eyOjWK',
      role: 'starter_studio_subscriber',
      maxUsers: 4,
      additionalSeatPrice: 25,
      additionalSeatMonthlyPriceId: 'price_1SbDOxP2MQvVS2rXMwvKjBFd',
      additionalSeatYearlyPriceId: 'price_1SbDOxP2MQvVS2rXnBlH1QLb',
      features: [
        'All Basic Features (per seat)',
        'Shared Client Profiles',
        'Studio Dashboard',
        'Invite & Manage Artists',
        'Role-Based Permissions',
        'Shared Form Templates',
        'Cross-Artist Calendar View',
        'Unified Billing',
        'Priority Support'
      ]
    },
    {
      id: 'growth_studio',
      name: 'Growth Studio',
      monthlyPrice: 299,
      yearlyPrice: 3289,
      monthlyPriceId: 'price_1SbDOzP2MQvVS2rXl7NLeJOX',
      yearlyPriceId: 'price_1SbDOzP2MQvVS2rXBuFLn3jb',
      role: 'growth_studio_subscriber',
      maxUsers: 4,
      additionalSeatPrice: 40,
      additionalSeatMonthlyPriceId: 'price_1SbDOtP2MQvVS2rXqeDl6qJx',
      additionalSeatYearlyPriceId: 'price_1SbDOtP2MQvVS2rXbCJg0W2L',
      features: [
        'Everything in Starter Studio',
        'Advanced Client Profiles',
        'Automated Client Follow-Ups',
        'Studio-wide Inventory',
        'Additional seats $40/month',
        'Priority Support'
      ]
    }
  ];

  // Only check once when component mounts
  useEffect(() => {
    if (user && !hasChecked.current) {
      hasChecked.current = true;
      checkUserStatusOnce();
    } else if (!user) {
      setCheckingSubscription(false);
    }
  }, [user]);

  const checkUserStatusOnce = async () => {
    if (!user) {
      setCheckingSubscription(false);
      return;
    }

    try {
      setCheckingSubscription(true);
      setStatusMessage('Checking subscription status...');
      
      const token = await getAccessTokenSilently();
      
      // Check subscription
      const subscriptionResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/subscription/details/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      let subscriptionData = null;
      if (subscriptionResponse.ok) {
        subscriptionData = await subscriptionResponse.json();
        setCurrentSubscription(subscriptionData);
        setStatusMessage(`Subscription check complete. Has subscription: ${subscriptionData?.has_subscription}`);
      } else {
        setStatusMessage('Subscription API failed - showing plans');
        setCurrentSubscription(null);
        setCheckingSubscription(false);
        return;
      }

      // If user has subscription, check profiles for onboarding completion
      if (subscriptionData?.has_subscription) {
        setStatusMessage('You have a subscription! Checking your profile...');
        
        // STUDIO MEMBER CHECK: Skip onboarding for studio members (not owners)
        if (subscriptionData?.is_studio_member && subscriptionData?.studio_membership?.role !== 'owner') {
          setStatusMessage('Welcome! Redirecting to dashboard...');
          setRedirectMessage('Welcome to the studio!');
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 1500);
          return;
        }
        
        // Check BOTH user profile AND shop profile (for owners and regular subscribers)
        const [userResponse, shopResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/user/profile/`, {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/shop/profile/`, {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
          })
        ]);
        
        if (userResponse.ok && shopResponse.ok) {
          const userData = await userResponse.json();
          const shopData = await shopResponse.json();
          // Check if onboarding is complete
          const needsOnboarding = !userData.first_name?.trim() || 
                                 !userData.last_name?.trim() || 
                                 !shopData.name?.trim() ||
                                 !shopData.address?.trim() ||
                                 !shopData.city?.trim() ||
                                 !shopData.state?.trim() ||
                                 !shopData.zip_code?.trim();
          if (needsOnboarding) {
            setStatusMessage('Profile incomplete, redirecting to onboarding...');
            setRedirectMessage('Setting up your profile...');
            setTimeout(() => {
              navigate('/onboarding', { replace: true });
            }, 1500);
          } else {
            setStatusMessage('Profile complete, redirecting to dashboard...');
            setRedirectMessage('Welcome back! Redirecting to dashboard...');
            setTimeout(() => {
              navigate('/dashboard', { replace: true });
            }, 1500);
          }
        } else {
          setStatusMessage('Profile check failed, redirecting to onboarding...');
          setRedirectMessage('Setting up your profile...');
          setTimeout(() => {
            navigate('/onboarding', { replace: true });
          }, 1500);
        }
      } else {
        // No subscription - show plans
        setStatusMessage('');
        setCheckingSubscription(false);
      }
      
    } catch (error) {
      console.error('❌ Error during status check:', error);
      setError(`Failed to check account status: ${error.message}`);
      setStatusMessage(`Error: ${error.message}`);
      setCheckingSubscription(false);
    }
  };

  const handleSubscribe = async (plan) => {
    try {
      setSelectedPlan(plan);
      setLoading(true);
      setError(null);
      
      const priceId = isYearly ? plan.yearlyPriceId : plan.monthlyPriceId;
      const token = await getAccessTokenSilently();
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/subscription/create-checkout-session/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_id: plan.id,
          price_id: priceId,
          mode: 'subscription',
          success_url: `${window.location.origin}/subscription-manager?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/subscription`
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create checkout session');
      }
      
      const data = await response.json();
      if (data.checkout_url || data.url) {
        window.location.href = data.checkout_url || data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
      
    } catch (error) {
      console.error('Subscription error:', error);
      setError(`Failed to create subscription: ${error.message}`);
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  // Calculate yearly savings
  const getYearlySavings = (plan) => {
    return (plan.monthlyPrice * 12) - plan.yearlyPrice;
  };

  const getSavingsPercent = (plan) => {
    const monthlyTotal = plan.monthlyPrice * 12;
    const savings = monthlyTotal - plan.yearlyPrice;
    return Math.round((savings / monthlyTotal) * 100);
  };

  // Show loading/checking state
  if (checkingSubscription) {
    return (
      <div className="subscription-plans-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <h2>Checking your account status...</h2>
          {statusMessage && <p>{statusMessage}</p>}
          {redirectMessage && (
            <div className="redirect-notice">
              <p>🚀 {redirectMessage}</p>
            </div>
          )}
          <div className="manual-options">
            <p>If this takes too long:</p>
            <button 
              onClick={() => {
                hasChecked.current = true;
                setCheckingSubscription(false);
              }}
              className="secondary-button"
            >
              Show Plans
            </button>
            <button 
              onClick={() => navigate('/onboarding')}
              className="secondary-button"
            >
              Go to Onboarding
            </button>
            <button 
              onClick={() => navigate('/dashboard')}
              className="secondary-button"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const PlanCard = ({ plan }) => {
    const isCurrentPlan = currentSubscription?.has_subscription && 
                         currentSubscription?.tier === plan.id;
    const canAddSeats = plan.additionalSeatPrice && (plan.id === 'starter_studio' || plan.id === 'growth_studio');

    return (
      <div className={`plan-card ${plan.popular ? 'popular' : ''} ${isCurrentPlan ? 'current-plan' : ''}`}>
        {plan.popular && <div className="popular-badge">Most Popular</div>}
        {isCurrentPlan && <div className="current-badge">Current Plan</div>}
        
        <div className="plan-header">
          <h2>{plan.name}</h2>
          <div className="price">
            ${isYearly ? plan.yearlyPrice : plan.monthlyPrice}
            <span className="period">/{isYearly ? 'year' : 'month'}</span>
          </div>
          
          {isYearly && (
            <div className="savings">
              Save ${getYearlySavings(plan)} ({getSavingsPercent(plan)}% off)
            </div>
          )}

          <div className="user-info">
            {plan.maxUsers === 1 
              ? 'Single user' 
              : `Up to ${plan.maxUsers} users included`
            }
            {canAddSeats && (
              <div className="additional-seats-info">
                Additional seats: ${plan.additionalSeatPrice}/month each
              </div>
            )}
          </div>
        </div>
        
        <div className="plan-features">
          <h3>Features:</h3>
          <ul className="features-list-no-checkmarks">
            {plan.features.map((feature, index) => (
              <li key={index}>{feature}</li>
            ))}
          </ul>
        </div>
        
        <button 
          onClick={() => handleSubscribe(plan)}
          disabled={loading && selectedPlan?.id === plan.id || isCurrentPlan}
          className={`subscribe-button ${isCurrentPlan ? 'current' : ''}`}
        >
          {isCurrentPlan 
            ? 'Current Plan' 
            : loading && selectedPlan?.id === plan.id 
            ? 'Processing...' 
            : 'Subscribe Now'
          }
        </button>
      </div>
    );
  };

  if (!user) {
    return (
      <div className="subscription-plans-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading user information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-plans-container">
      <h1>Choose Your Subscription Plan</h1>
      <p className="plans-description">Scale your tattoo studio with our flexible pricing options</p>
      
      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
          <button onClick={() => setError(null)} className="dismiss-error">✕</button>
        </div>
      )}

      {/* Billing Toggle */}
      <div className="billing-toggle">
        <span className={`billing-option ${!isYearly ? 'active' : ''}`}>Monthly</span>
        <button
          onClick={() => setIsYearly(!isYearly)}
          className={`toggle-switch ${isYearly ? 'yearly' : 'monthly'}`}
          aria-label="Toggle billing period"
        >
          <div className="toggle-slider" />
        </button>
        <span className={`billing-option ${isYearly ? 'active' : ''}`}>Yearly</span>
        {isYearly && (
          <span className="savings-badge">Save up to 8%</span>
        )}
      </div>

      {/* Current Subscription Info */}
      {currentSubscription?.has_subscription && (
        <div className="current-subscription-info">
          <h3>Current Subscription</h3>
          <p>You're currently on the <strong>{currentSubscription.tier?.replace('_', ' ')}</strong> plan</p>
          <button 
            onClick={() => navigate('/subscription-manager')}
            className="manage-subscription-link"
          >
            Manage Subscription →
          </button>
        </div>
      )}
      
      <div className="plans-wrapper">
        {plans.map((plan) => (
          <PlanCard key={plan.id} plan={plan} />
        ))}
      </div>
      
      <div className="secure-payment">
        <p>🔒 Secure payment processed by Stripe</p>
        <p>Cancel anytime. No setup fees.</p>
      </div>
    </div>
  );
};

export default SubscriptionPlans;